import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { EmptyRow } from '@/features/EmptyRow';
import { CompanyContactType, type CompanyContactRow } from '@/core/models/report-settings.models';

export interface ContactSelectionChange {
  /** Empty means every contact in the grid. */
  ids: string[];
  isSelected: boolean;
}

interface Props {
  contacts: CompanyContactRow[];
  contactType?: number;
  shareInPercentage?: boolean;
  /** Partnership: Shares (%), Salary, Interest on capital and Interest on Drawings columns (BUG-078). */
  partnerTerms?: boolean;
  onEdit: (contact: CompanyContactRow) => void;
  onDelete: (contact: CompanyContactRow) => void;
  onSelectionChange: (change: ContactSelectionChange) => void;
  /** What this table lists, in the plural and lower case ("directors"). Set once the contacts have been read: an empty table then says so. */
  emptyLabel?: string;
}

const address = (contact: CompanyContactRow) =>
  [contact.addressLine, contact.addressLine2, contact.city, contact.county, contact.postCode, contact.country]
    .filter((part) => !!part)
    .join(', ');

const contactName = (contact: CompanyContactRow) =>
  contact.fullName || [contact.firstName, contact.lastName].filter(Boolean).join(' ') || '';

const numberText = (value: number | null | undefined) => (value === null || value === undefined ? '' : `${value}`);

/** Angular CompanyContactGridComponent: one contact type's table with a select-all header and per-row edit/delete. */
export function CompanyContactGrid({
  contacts,
  contactType = CompanyContactType.Director,
  shareInPercentage = false,
  partnerTerms = false,
  onEdit,
  onDelete,
  onSelectionChange,
  emptyLabel,
}: Props) {
  const allSelected = contacts.length > 0 && contacts.every((c) => c.isSelected);
  const someSelected = contacts.some((c) => c.isSelected) && !allSelected;

  const share = (contact: CompanyContactRow): string => {
    if (
      contactType !== CompanyContactType.Director ||
      contact.ordinaryShares === null ||
      contact.ordinaryShares === undefined
    ) {
      return '';
    }
    if (!shareInPercentage) {
      return `${contact.ordinaryShares}`;
    }
    const total = contacts.reduce((sum, c) => sum + (c.ordinaryShares ?? 0), 0);
    return total > 0 ? `${((contact.ordinaryShares / total) * 100).toFixed(2)}%` : '0%';
  };

  const columns: DataTableColumn<CompanyContactRow>[] = [
    {
      id: 'name',
      header: (
        <div className="d-flex align-items-center gap-10">
          <Checkbox
            disabled={contacts.length === 0}
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(event) => onSelectionChange({ ids: [], isSelected: event.target.checked })}
          />
          Name
        </div>
      ),
      cell: (element) => (
        <div className="d-flex align-items-center gap-10">
          <Checkbox
            checked={element.isSelected}
            onChange={(event) => onSelectionChange({ ids: [element.id], isSelected: event.target.checked })}
          />
          <span className="text-ellipsis-cell" title={contactName(element)}>
            {contactName(element)}
          </span>
        </div>
      ),
    },
    {
      id: 'address',
      header: 'Address',
      cell: (element) => (
        <span className="text-ellipsis-cell" title={address(element)}>
          {address(element)}
        </span>
      ),
    },
    ...(partnerTerms
      ? ([
          { id: 'share', header: 'Shares(%)', cell: (element) => numberText(element.ordinaryShares) },
          { id: 'salary', header: 'Salary', cell: (element) => numberText(element.salary) },
          {
            id: 'interestOnCapital',
            header: 'Interest on capital',
            cell: (element) => numberText(element.interestOnCapital),
          },
          {
            id: 'interestOnDrawings',
            header: 'Interest on Drawings',
            cell: (element) => numberText(element.interestOnDrawings),
          },
        ] satisfies DataTableColumn<CompanyContactRow>[])
      : ([
          { id: 'share', header: 'Share', cell: (element) => share(element) },
        ] satisfies DataTableColumn<CompanyContactRow>[])),
    {
      id: 'action',
      header: 'Action',
      cell: (element) => (
        <div className="d-flex align-items-center grid-actions">
          <Button title="Edit" onClick={() => onEdit(element)}>
            <Icon name="edit" outlined />
          </Button>
          <Button title="Delete" onClick={() => onDelete(element)}>
            <Icon name="delete_outline" outlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mat-table-wrapper">
        <DataTable
          columns={columns}
          rows={contacts}
          rowKey={(row) => row.id}
          stickyHeader
          noDataMessage={
            emptyLabel ? <EmptyRow icon="groups" title={`No ${emptyLabel} added yet`} hint="Use the Add button above to add the first one." /> : undefined
          }
        />
      </div>
    </div>
  );
}
